import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import type { ClientePrecoProdutoApi, SalvarPrecoProdutoRequest } from "../types";
import { toast } from "sonner";

export const usePrecosProduto = (personId: string | undefined) => {
  const queryClient = useQueryClient();
  const numId = personId ? Number(personId) : undefined;
  const key = ["person-precos-produto", numId];

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data } = await apiClient.get<ClientePrecoProdutoApi[]>(`/api/clientes/${numId}/precos-produto`);
      return data;
    },
    enabled: !!numId,
  });

  const createMutation = useMutation({
    mutationFn: async (body: SalvarPrecoProdutoRequest) => {
      if (!numId) throw new Error("Person ID is required");
      const { data } = await apiClient.post<ClientePrecoProdutoApi>(`/api/clientes/${numId}/precos-produto`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      toast.success("Preço diferenciado adicionado!");
    },
    onError: (e: Error) => toast.error(`Erro ao adicionar: ${e.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: SalvarPrecoProdutoRequest }) => {
      const { data: updated } = await apiClient.put<ClientePrecoProdutoApi>(`/api/clientes/${numId}/precos-produto/${id}`, data);
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      toast.success("Preço diferenciado atualizado!");
    },
    onError: (e: Error) => toast.error(`Erro ao atualizar: ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => { await apiClient.delete(`/api/clientes/${numId}/precos-produto/${id}`); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      toast.success("Preço diferenciado removido!");
    },
    onError: (e: Error) => toast.error(`Erro ao remover: ${e.message}`),
  });

  return {
    precos:      query.data ?? [],
    isLoading:   query.isLoading,
    createPreco: createMutation.mutate,
    updatePreco: updateMutation.mutate,
    deletePreco: deleteMutation.mutate,
    isCreating:  createMutation.isPending,
  };
};
